import Stats from 'stats.js'
import * as THREE from 'three'
import type { FrameDriver } from '@/graphics/FrameDriver'
import type { GraphicsBackend } from '@/graphics/GraphicsBackend'
import { defineLegacyRendererAccessor } from '@/graphics/legacyGraphicsAccessors'
import { WebGLFrameDriver } from '@/graphics/WebGLFrameDriver'
import { getLoggerManager } from '@/shared/logging'
import type { Disposable } from '@/shared/types/core'
import type {
  RenderInvalidationService,
  ViewManagedCamera,
  ViewportCamera,
  ViewRegistry,
} from '@/shared/types/services'
import type {
  EngineFrameContext,
  ViewFrameContext,
  ViewRenderPolicy,
  ViewSnapshot,
} from '@/shared/types/view'
import { toRendererViewportRect } from '@/shared/utils/viewRect'
import type { EngineContext } from './EngineContext'
import type { EventBus } from './EventBus'
import type { PluginManager } from './PluginManager'

const log = getLoggerManager().getLogger('runtime')

interface RenderSurfaceInfo {
  containerWidth: number
  containerHeight: number
  renderWidth: number
  renderHeight: number
  canvasWidth: number
  canvasHeight: number
  useRendererSurface: boolean
}

interface ViewportSize {
  width: number
  height: number
}

export interface ManualRenderFrameRequest extends ViewportSize {
  timestampSeconds: number
  deltaSeconds: number
  frameNumber: number
  pixelRatio: number
  viewId?: string
}

interface InternalFrameRequest {
  timestamp: number
  delta: number
  frameNumber: number
  forceRenderAllViews: boolean
  syncCachedViewport: boolean
  targetViewId?: string
  targetViewIds?: ReadonlySet<string>
  manualFrame?: {
    deterministic: boolean
  }
}

type RenderMode = 'continuous' | 'on-demand'

/** delta 上限只为吸收卡顿尖峰（GC、标签页切回），不能惩罚持续低帧率设备，否则全局慢动作 */
const MAX_FRAME_DELTA_SECONDS = 0.1

function normalizePixelRatioOverride(pixelRatio?: number | null): number | null {
  if (pixelRatio === undefined || pixelRatio === null) {
    return null
  }

  return Number.isFinite(pixelRatio) && pixelRatio > 0 ? pixelRatio : 1
}

/**
 * Frame loop: runs global update once per RAF, then renders each visible view once.
 */
export class RenderLoop implements Disposable {
  private _running = false
  private _disposed = false
  private _fps = 0

  private readonly context: EngineContext
  private readonly graphics: GraphicsBackend
  private readonly pluginManager: PluginManager
  private readonly events: EventBus
  private readonly viewportCamera: ViewportCamera | null
  private readonly viewRegistry: ViewRegistry | null
  private readonly renderInvalidation: RenderInvalidationService | null
  private pipelineActivityDisposable: Disposable | null
  private readonly renderMode: RenderMode
  private readonly frameDriver: FrameDriver
  private readonly fallbackCamera = new THREE.PerspectiveCamera(60, 1, 0.1, 10_000)
  /** 只承载非纯色 scene.background，避免为绘制背景重放 mainScene 中的资产节点。 */
  private readonly backgroundScene = new THREE.Scene()
  private _stats: Stats | null = null

  private lastTime = 0
  private resizeObserver: ResizeObserver | null = null
  private cachedViewportSize: ViewportSize = { width: 0, height: 0 }
  private readonly lastRenderedAt = new Map<string, number>()
  private readonly pixelRatioOverride: number | null

  /** 帧循环是否正在运行 */
  get running(): boolean {
    return this._running
  }

  /** 最近一帧计算得到的瞬时 FPS */
  get fps(): number {
    return this._fps
  }

  /** 获取帧循环持有状态的诊断快照。 */
  getMemoryDiagnostics(): {
    running: boolean
    disposed: boolean
    rafScheduled: boolean
    resizeObserverActive: boolean
    renderedViewEntries: number
  } {
    return {
      running: this._running,
      disposed: this._disposed,
      rafScheduled: this.frameDriver.scheduled,
      resizeObserverActive: this.resizeObserver !== null,
      renderedViewEntries: this.lastRenderedAt.size,
    }
  }

  constructor(
    context: EngineContext,
    pluginManager: PluginManager,
    viewportCamera: ViewportCamera | null,
    pixelRatioOverride?: number | null,
    renderMode: RenderMode = 'continuous',
    frameDriver?: FrameDriver
  ) {
    this.context = context
    this.graphics = context.graphics
    this.pluginManager = pluginManager
    this.events = context.events
    this.viewportCamera = viewportCamera
    this.renderMode = renderMode
    this.frameDriver =
      frameDriver ??
      new WebGLFrameDriver({
        mode: renderMode,
        onError: (error, phase) => {
          log.error(
            `event=frameDriver.error backend=${this.graphics.kind} phase=${phase} errorName=${error.name}`
          )
        },
      })
    this.pixelRatioOverride = normalizePixelRatioOverride(pixelRatioOverride)
    this.viewRegistry = context.hasService('ViewRegistry')
      ? context.getService('ViewRegistry')
      : null
    this.renderInvalidation = context.hasService('RenderInvalidationService')
      ? context.getService('RenderInvalidationService')
      : null
    if (this.renderMode === 'on-demand') {
      this.renderInvalidation?.setFrameRequestCallback(() => this.requestFrame())
    }
    if (context.hasService('RenderPipeline')) {
      this.pipelineActivityDisposable =
        this.renderInvalidation?.registerActivitySource({
          id: 'RenderPipeline',
          getRenderActivity: (activityContext) =>
            context.getService('RenderPipeline').collectRenderActivity(activityContext),
        }) ?? null
    } else {
      this.pipelineActivityDisposable = null
    }
    this.cachedViewportSize = this.readContainerSize()

    this.resizeObserver = new ResizeObserver((entries?: ResizeObserverEntry[]) => {
      this.handleResizeEntries(entries)
    })
    this.resizeObserver.observe(context.container)
  }

  /**
   * 启用或禁用性能统计面板。
   *
   * @param container - Stats 面板挂载容器，传 null 卸载面板
   */
  enableStats(container: HTMLElement | null): void {
    if (container && !this._stats) {
      this._stats = new Stats()
      this._stats.showPanel(0)
      this._stats.dom.style.position = 'relative'
      container.appendChild(this._stats.dom)
    } else if (!container && this._stats) {
      this._stats.dom.remove()
      this._stats = null
    }
  }

  /** 启动帧循环，重复调用或已销毁时忽略 */
  start(): void {
    if (this._running || this._disposed) return
    this._running = true
    this.lastTime = performance.now()
    this.frameDriver.start((timestamp) => this.handleDriverFrame(timestamp))
    if (this.renderMode === 'on-demand') {
      this.renderInvalidation?.invalidate('engine.initialFrame')
      if (!this.renderInvalidation) {
        this.requestFrame()
      }
    }
  }

  /** 停止帧循环，取消下一帧调度 */
  stop(): void {
    this._running = false
    this.frameDriver.stop()
    this.renderInvalidation?.setFrameScheduled(false)
  }

  /** 立即同步执行一帧，用于截图等离屏场景 */
  renderOnce(): void {
    if (this._disposed) return
    const now = performance.now()
    if (this.lastTime === 0) this.lastTime = now
    // renderOnce 是同步 one-shot 渲染入口（截图/离屏读取等），不接管 on-demand 后续调度。
    this.onFrame(now, { forceRender: true, collectActivity: false })
  }

  renderManualFrame(request: ManualRenderFrameRequest): void {
    if (this._disposed) return
    this.validateManualFrameRequest(request)
    this.stop()

    const timestamp = request.timestampSeconds * 1000
    const delta = request.deltaSeconds
    const width = Math.round(request.width)
    const height = Math.round(request.height)
    this.updateCachedViewportSize({ width, height })
    this.applyViewport({
      width,
      height,
      pixelRatio: request.pixelRatio,
    })
    this.lastTime = timestamp
    this.runFrame({
      timestamp,
      delta,
      frameNumber: request.frameNumber,
      forceRenderAllViews: true,
      syncCachedViewport: false,
      targetViewId: request.viewId,
      manualFrame: {
        deterministic: true,
      },
    })
  }

  /** 在非运行状态下请求执行下一帧，用于按需渲染 */
  requestFrame(): void {
    if (this._disposed) return
    if (this.renderMode === 'continuous' && this._running) return
    this.renderInvalidation?.setFrameScheduled(true)
    this.frameDriver.invalidate()
  }

  /** 同步 renderer 尺寸和像素比到容器当前大小，并广播 viewport.resized */
  syncViewport(size?: ViewportSize): void {
    this.updateCachedViewportSize(size ?? this.readContainerSize())
    if (this.applyCachedViewport()) {
      this.renderInvalidation?.invalidate('viewport.resized')
    }
  }

  private readContainerSize(): ViewportSize {
    return this.normalizeViewportSize(this.context.getSize())
  }

  private handleResizeEntries(entries?: ResizeObserverEntry[]): void {
    const observedSize = this.getObservedViewportSize(entries)
    this.updateCachedViewportSize(observedSize ?? this.readContainerSize())
    if (this.applyCachedViewport()) {
      this.renderInvalidation?.invalidate('viewport.resized')
    }
  }

  private getObservedViewportSize(entries?: ResizeObserverEntry[]): ViewportSize | null {
    const entry = entries?.[0]
    if (!entry) {
      return null
    }

    const boxSize = entry.contentBoxSize
    const firstBox = Array.isArray(boxSize) ? boxSize[0] : boxSize
    if (firstBox) {
      return this.normalizeViewportSize({
        width: firstBox.inlineSize,
        height: firstBox.blockSize,
      })
    }

    if (entry.contentRect) {
      return this.normalizeViewportSize({
        width: entry.contentRect.width,
        height: entry.contentRect.height,
      })
    }

    return null
  }

  private normalizeViewportSize(size: ViewportSize): ViewportSize {
    return {
      width: Math.max(Math.round(size.width), 1),
      height: Math.max(Math.round(size.height), 1),
    }
  }

  private updateCachedViewportSize(size: ViewportSize): void {
    if (
      size.width === this.cachedViewportSize.width &&
      size.height === this.cachedViewportSize.height
    ) {
      return
    }

    this.cachedViewportSize = size
  }

  private applyCachedViewport(): boolean {
    const { width, height } = this.cachedViewportSize
    return this.applyViewport({
      width,
      height,
      pixelRatio: this.pixelRatioOverride ?? undefined,
    })
  }

  private applyViewport(viewport: { width: number; height: number; pixelRatio?: number }): boolean {
    const { width, height, pixelRatio } = viewport
    const changed = this.graphics.resize({
      width,
      height,
      pixelRatio,
      devicePixelRatio: window.devicePixelRatio || 1,
    })

    if (changed) {
      if (this.context.hasService('RenderPipeline')) {
        this.context.getService('RenderPipeline').resize(width, height)
      }

      this.events.emit('viewport.resized', {
        width,
        height,
        pixelRatio: this.graphics.surface.pixelRatio,
      })
    }
    return changed
  }

  /** 停止帧循环并释放 ResizeObserver、Stats 面板等持有资源 */
  dispose(): void {
    if (this._disposed) {
      return
    }
    this._disposed = true
    this.stop()
    this.frameDriver.dispose()
    this.pipelineActivityDisposable?.dispose()
    this.pipelineActivityDisposable = null
    this.renderInvalidation?.setFrameRequestCallback(null)
    this.lastRenderedAt.clear()
    this.enableStats(null)
    this.resizeObserver?.disconnect()
    this.resizeObserver = null
    this.backgroundScene.background = null
  }

  private handleDriverFrame(timestamp: DOMHighResTimeStamp): void {
    this.renderInvalidation?.setFrameScheduled(false)
    if (!this._running || this._disposed) return
    try {
      if (this.lastTime === 0) this.lastTime = timestamp
      this.onFrame(timestamp, {
        forceRender: this.renderMode === 'on-demand',
        collectActivity: this.renderMode === 'on-demand',
      })
    } catch (error) {
      log.error(`[RenderLoop] onFrame error: ${String(error)}`)
    }
  }

  /**
   * 在资产贡献者之前绘制当前 Three.js 非纯色场景背景。
   *
   * 主资产由各插件通过隔离场景分别渲染，不能直接重放 mainScene；这里使用一个空场景复用
   * mainScene.background。纯色背景已合并进 clearView，Texture 等背景仍通过空场景进入
   * 每个视口及手动视频帧的实际输出。
   */
  private renderViewBackground(viewFrame: ViewFrameContext): void {
    const background = this.context.sceneGraph.mainScene.background
    const renderScene = this.graphics.renderScene
    if (
      background === null ||
      background instanceof THREE.Color ||
      typeof renderScene !== 'function'
    ) {
      return
    }

    const graphicsState = this.graphics.captureState(this)
    this.backgroundScene.background = background
    try {
      renderScene.call(this.graphics, this.backgroundScene, viewFrame.camera)
    } finally {
      this.backgroundScene.background = null
      this.graphics.restoreState(graphicsState, this)
    }
  }

  private onFrame(
    timestamp: number,
    options: { forceRender?: boolean; collectActivity?: boolean } = {}
  ): readonly string[] {
    const dirtyReasons = this.renderInvalidation?.consumeDirty() ?? []
    if (this.renderMode === 'on-demand' && !options.forceRender && dirtyReasons.length === 0) {
      return []
    }

    const rawDelta = Math.max(0, (timestamp - this.lastTime) / 1000)
    const delta = Math.min(rawDelta, MAX_FRAME_DELTA_SECONDS)
    this.lastTime = timestamp
    // fps 反映真实帧率，使用未截断的 rawDelta；截断后的 delta 仅用于驱动模拟更新
    if (rawDelta > 0) {
      this._fps = 1 / rawDelta
    }
    const renderedViewIds = this.runFrame({
      timestamp,
      delta,
      frameNumber: this.context.frameNumber + 1,
      forceRenderAllViews: false,
      syncCachedViewport: true,
      // View-scoped invalidation is only a valid optimization for a single
      // renderable view. Multi-view frames share one surface, and the pixels of
      // an unrendered viewport are not guaranteed to survive presentation.
      targetViewIds:
        this.renderMode === 'on-demand' ? this.resolveInvalidatedViewIds(dirtyReasons) : undefined,
    })
    if (options.collectActivity ?? this.renderMode === 'on-demand') {
      this.collectOnDemandActivity(renderedViewIds)
    }
    return renderedViewIds
  }

  private runFrame(request: InternalFrameRequest): readonly string[] {
    if (!this.graphics.canSubmit) {
      return []
    }
    const graphicsGeneration = this.graphics.beginFrame()
    this._stats?.begin()
    const { timestamp, delta } = request
    this.context.frameNumber = request.frameNumber
    const renderedViewIds: string[] = []
    try {
      if (request.syncCachedViewport) {
        this.applyCachedViewport()
      }

      const surface = this.getRenderSurfaceInfo()
      const width = surface.renderWidth
      const height = surface.renderHeight
      const engineFrame = defineLegacyRendererAccessor(
        {
          frameNumber: this.context.frameNumber,
          timestamp,
          delta,
          graphics: this.graphics,
          views: this.collectViewSnapshots(width, height),
          manualFrame: request.manualFrame,
        },
        this.graphics,
        () => this.context.renderer
      ) satisfies EngineFrameContext
      this.pruneRenderSchedule(engineFrame.views)
      const targetViewIds = this.resolveFrameTargetViewIds(request.targetViewIds, engineFrame.views)
      if (request.targetViewId && !this.resolveRenderableView(request.targetViewId)) {
        throw new Error(`Manual frame target view is not renderable: ${request.targetViewId}`)
      }

      this.events.emit('frame.begin', { timestamp, delta })
      this.pluginManager.updateFrame(engineFrame)

      const preparedViews: ViewFrameContext[] = []
      const drawViewIds: string[] = []
      for (const snapshot of engineFrame.views) {
        const current = this.resolveRenderableView(snapshot.id)
        if (!current) {
          continue
        }

        const viewFrame = this.createViewFrame(
          current,
          timestamp,
          delta,
          surface,
          request.manualFrame
        )
        preparedViews.push(viewFrame)
        if (request.targetViewId && current.id !== request.targetViewId) {
          continue
        }
        if (targetViewIds && !targetViewIds.has(current.id)) {
          continue
        }
        if (!request.forceRenderAllViews && !this.shouldRenderView(current, timestamp)) {
          continue
        }
        drawViewIds.push(current.id)
      }
      this.pluginManager.prepareViews?.(preparedViews, drawViewIds)

      const drawViewIdSet = new Set(drawViewIds)
      for (const viewFrame of preparedViews) {
        if (!drawViewIdSet.has(viewFrame.viewId)) {
          continue
        }

        const graphicsState = this.graphics.captureState(this)
        let viewBegan = false
        let eventBegan = false
        try {
          this.graphics.beginView(viewFrame.viewRect)
          viewBegan = true
          const background = this.context.sceneGraph.mainScene.background
          this.graphics.clearView({
            clearColor: background instanceof THREE.Color ? background : 0x000000,
            clearAlpha: background instanceof THREE.Color ? 1 : 0,
          })
          this.renderViewBackground(viewFrame)
          this.events.emit('view.frame.begin', { viewId: viewFrame.viewId, timestamp, delta })
          eventBegan = true
          this.pluginManager.renderView(viewFrame)
          this.lastRenderedAt.set(viewFrame.viewId, timestamp)
          renderedViewIds.push(viewFrame.viewId)
        } finally {
          if (viewBegan) {
            this.graphics.endView()
          }
          this.graphics.restoreState(graphicsState, this)
          if (eventBegan) {
            this.events.emit('view.frame.end', {
              viewId: viewFrame.viewId,
              frameContext: viewFrame,
            })
          }
        }
      }

      this.events.emit('frame.end', { engineFrame })
      return renderedViewIds
    } finally {
      this._stats?.end()
      this.graphics.endFrame(graphicsGeneration)
    }
  }

  private collectOnDemandActivity(renderedViewIds: readonly string[]): void {
    if (this.renderMode !== 'on-demand' || !this._running || !this.renderInvalidation) {
      return
    }

    const activity = this.renderInvalidation.collectActivity({
      frameNumber: this.context.frameNumber,
      renderedViewIds,
      activeViewId: this.viewRegistry?.getActive()?.id ?? null,
    })
    if (!activity.needsNextFrame) {
      return
    }

    const detail = activity.reasons?.join(',') ?? ''
    if (activity.viewIds?.length) {
      for (const viewId of activity.viewIds) {
        this.renderInvalidation.requestNextFrame('render.activity', { viewId, detail })
      }
      return
    }
    this.renderInvalidation.requestNextFrame('render.activity', { detail })
  }

  private resolveFrameTargetViewIds(
    targetViewIds: ReadonlySet<string> | undefined,
    views: readonly ViewSnapshot[]
  ): ReadonlySet<string> | undefined {
    if (!targetViewIds) {
      return undefined
    }

    const renderableViewCount = views.reduce(
      (count, view) => count + (this.resolveRenderableView(view.id) ? 1 : 0),
      0
    )
    return renderableViewCount > 1 ? undefined : targetViewIds
  }

  private resolveInvalidatedViewIds(
    entries: readonly { viewId?: string | null }[]
  ): ReadonlySet<string> | undefined {
    if (entries.length === 0 || entries.some((entry) => !entry.viewId)) {
      return undefined
    }
    return new Set(entries.map((entry) => entry.viewId as string))
  }

  private validateManualFrameRequest(request: ManualRenderFrameRequest): void {
    const invalidField = Object.entries({
      frameNumber: request.frameNumber,
      timestampSeconds: request.timestampSeconds,
      deltaSeconds: request.deltaSeconds,
      width: request.width,
      height: request.height,
      pixelRatio: request.pixelRatio,
    }).find(([, value]) => !Number.isFinite(value))

    if (invalidField) {
      throw new Error(`Invalid manual frame parameter: ${invalidField[0]}`)
    }
    if (!Number.isInteger(request.frameNumber) || request.frameNumber < 0) {
      throw new Error('Invalid manual frame parameter: frameNumber')
    }
    if (request.timestampSeconds < 0 || request.deltaSeconds < 0) {
      throw new Error('Invalid manual frame parameter: timestampSeconds/deltaSeconds')
    }
    if (request.width <= 0 || request.height <= 0 || request.pixelRatio <= 0) {
      throw new Error('Invalid manual frame parameter: width/height/pixelRatio')
    }
  }

  private collectViewSnapshots(width: number, height: number): readonly ViewSnapshot[] {
    if (this.viewRegistry) {
      return this.viewRegistry.list()
    }

    return [
      {
        id: 'primary',
        rect: { x: 0, y: 0, width, height },
        visible: true,
        zIndex: 0,
        layerMask: 1,
        userData: {},
        removable: false,
        active: true,
      },
    ]
  }

  private resolveRenderableView(viewId: string): ViewSnapshot | null {
    if (!this.viewRegistry) {
      return (
        this.collectViewSnapshots(
          this.cachedViewportSize.width,
          this.cachedViewportSize.height
        )[0] ?? null
      )
    }

    const snapshot = this.viewRegistry.get(viewId)
    if (!snapshot) {
      return null
    }
    if (!snapshot.visible || snapshot.rect.width <= 0 || snapshot.rect.height <= 0) {
      return null
    }
    return snapshot
  }

  private shouldRenderView(snapshot: ViewSnapshot, timestamp: number): boolean {
    if (snapshot.active || !this.viewRegistry) {
      return true
    }

    const policy = this.viewRegistry.getExtension(snapshot.id, 'renderPolicy') as
      | ViewRenderPolicy
      | null
      | undefined
    const minIntervalMs = policy?.minIntervalMs ?? 0
    if (minIntervalMs <= 0) {
      return true
    }

    const lastRenderedAt = this.lastRenderedAt.get(snapshot.id)
    if (lastRenderedAt == null) {
      return true
    }

    return timestamp - lastRenderedAt >= minIntervalMs
  }

  private pruneRenderSchedule(views: readonly ViewSnapshot[]): void {
    const aliveViewIds = new Set(views.map((view) => view.id))
    for (const viewId of Array.from(this.lastRenderedAt.keys())) {
      if (!aliveViewIds.has(viewId)) {
        this.lastRenderedAt.delete(viewId)
      }
    }
  }

  private createViewFrame(
    snapshot: ViewSnapshot,
    timestamp: number,
    delta: number,
    surface: RenderSurfaceInfo,
    manualFrame?: {
      deterministic: boolean
    }
  ): ViewFrameContext {
    const viewRect = this.getEffectiveViewRect(snapshot.rect, surface)
    const camera = this.getViewCamera(snapshot.id)
    this.syncCameraForBackend(camera, viewRect)
    const viewport =
      this.graphics.kind === 'webgl2'
        ? toRendererViewportRect(viewRect, {
            width: surface.renderWidth,
            height: surface.renderHeight,
          })
        : { ...viewRect }
    return defineLegacyRendererAccessor(
      {
        frameNumber: this.context.frameNumber,
        timestamp,
        delta,
        graphics: this.graphics,
        viewId: snapshot.id,
        camera,
        viewport,
        viewRect,
        manualFrame,
      },
      this.graphics,
      () => this.context.renderer
    )
  }

  private getViewCamera(viewId: string): THREE.Camera {
    const managedCamera = this.viewRegistry?.getExtension(viewId, 'viewportCamera') as
      | ViewManagedCamera
      | undefined
    return (
      managedCamera?.getActiveCamera() ??
      this.viewportCamera?.getActiveCamera() ??
      this.fallbackCamera
    )
  }

  private getRenderSurfaceInfo(): RenderSurfaceInfo {
    const graphicsSurface = this.graphics.surface
    const renderWidth = graphicsSurface.logicalWidth
    const renderHeight = graphicsSurface.logicalHeight
    const useRendererSurface =
      renderWidth !== this.cachedViewportSize.width ||
      renderHeight !== this.cachedViewportSize.height

    return {
      containerWidth: this.cachedViewportSize.width,
      containerHeight: this.cachedViewportSize.height,
      renderWidth,
      renderHeight,
      canvasWidth: graphicsSurface.drawingBufferWidth,
      canvasHeight: graphicsSurface.drawingBufferHeight,
      useRendererSurface,
    }
  }

  private syncCameraForBackend(camera: THREE.Camera, viewRect: ViewportSize): void {
    const coordinateSystem =
      this.graphics.kind === 'webgpu' ? THREE.WebGPUCoordinateSystem : THREE.WebGLCoordinateSystem
    const viewAspect = viewRect.height > 0 ? viewRect.width / viewRect.height : 1

    const sync = (current: THREE.Camera, aspect: number): void => {
      current.coordinateSystem = coordinateSystem
      if ('aspect' in current && typeof current.aspect === 'number') {
        current.aspect = aspect
      }
      if (
        'updateProjectionMatrix' in current &&
        typeof current.updateProjectionMatrix === 'function'
      ) {
        current.updateProjectionMatrix()
      }

      if ((current as THREE.ArrayCamera).isArrayCamera) {
        for (const child of (current as THREE.ArrayCamera).cameras) {
          const viewport = child.viewport
          const childAspect = viewport && viewport.w > 0 ? viewport.z / viewport.w : aspect
          sync(child, childAspect)
        }
      }
    }

    sync(camera, viewAspect)
  }

  private getEffectiveViewRect(
    rect: { x: number; y: number; width: number; height: number },
    surface: RenderSurfaceInfo
  ): { x: number; y: number; width: number; height: number } {
    if (!surface.useRendererSurface) {
      return rect
    }

    const scaleX = surface.containerWidth > 0 ? surface.renderWidth / surface.containerWidth : 1
    const scaleY = surface.containerHeight > 0 ? surface.renderHeight / surface.containerHeight : 1

    return {
      x: Math.round(rect.x * scaleX),
      y: Math.round(rect.y * scaleY),
      width: Math.round(rect.width * scaleX),
      height: Math.round(rect.height * scaleY),
    }
  }
}
